import { useCallback, useEffect, useMemo, useRef } from 'react'
import { getCapitalizedText } from '~utils/string'
import { GroupCheckboxProps, GroupItem } from '../../types'
import CheckSelect from '../ui/check-select'

function getGroupItems<T extends GroupItem>(items: T[]): Record<string, T[]> {
  return items.reduce(
    (acc, cur) => {
      const groupName = cur.groupBy ?? 'default'
      acc[groupName] = acc[groupName] ?? []
      acc[groupName].push(cur)
      return acc
    },
    {} as Record<string, T[]>
  )
}

const SelectCheckboxOptions = ({
  root,
  datafield,
  options,
  onSelect,
  optionKey,
}: Readonly<GroupCheckboxProps>) => {
  const initialized = useRef(false)
  const hasInitialValue = useMemo(() => {
    return Object.values(root).some(Boolean)
  }, [root])

  const groupByItems = useMemo(() => {
    if (Array.isArray(options)) {
      return getGroupItems(options)
    }

    if (!optionKey || typeof options[optionKey] === 'undefined') {
      return getGroupItems(options.Extra)
    }

    return getGroupItems(options[optionKey])
  }, [options, optionKey])

  const _onSelect = useCallback(
    (selectedItem: GroupItem, isUniqueValue = false) => {
      let reset_other_datafields = false
      let newValue = selectedItem.value
      if (!isUniqueValue) {
        const prevValue = datafield
          ? root[selectedItem.datafield || datafield]
          : (root as any[] | undefined)
        if (prevValue) {
          if (Array.isArray(prevValue)) {
            if (prevValue.includes(selectedItem.value)) {
              newValue = (prevValue as any[]).filter(value => value !== selectedItem.value)

              if (selectedItem.extra_component) {
                reset_other_datafields = true
              }
            } else {
              newValue = [...prevValue, selectedItem.value]
            }
          } else {
            newValue = [selectedItem.value]
          }
        } else {
          newValue = [selectedItem.value]
        }
      }

      onSelect({
        ...selectedItem,
        value: newValue,
        datafield: selectedItem.datafield || datafield,
        reset_other_datafields,
      })
    },
    [datafield, onSelect, root]
  )

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true

      if (!hasInitialValue) {
        onSelect({
          value: [],
          datafield,
          reset_other_datafields: false,
        })
      }
    }
  }, [_onSelect, datafield, hasInitialValue, onSelect])

  return (
    <section className="flex flex-col flex-1 flex-gap-y-4">
      {Object.keys(groupByItems).map((groupName, index) => (
        <div key={index} className="flex-1 flex flex-col">
          {groupName !== 'default' && (
            <div className="mb-4 pb-2">
              <span className="text-label-1 text-gray-700">{getCapitalizedText(groupName)}</span>
            </div>
          )}
          <div className="flex flex-col flex-gap-y-4">
            {groupByItems[groupName].map((renderItem, itemIndex) => {
              const value = !datafield
                ? root
                : renderItem.datafield
                  ? root[renderItem.datafield]
                  : root[datafield]
              const isSelected = Array.isArray(value) && value.includes(renderItem.value)
              return (
                <CheckSelect
                  key={itemIndex}
                  root={root}
                  item={renderItem}
                  onSelect={_onSelect}
                  isSelected={isSelected}
                />
              )
            })}
          </div>
        </div>
      ))}
    </section>
  )
}

SelectCheckboxOptions.displayName = 'GroupCheckbox'
export default SelectCheckboxOptions
